import { authService } from '../service/auth/auth-service'
import { authConfig as config } from '../util/auth-config'
import { logger } from '../util/logger'
import { toasterUtil } from '../util/toaster-util'
import { queryKeys } from '../util/query-keys'
import { useMutation } from '@tanstack/react-query'

const GOOGLE_SIGN_IN_ERROR_MESSAGE = 'Google Sign In failed. Please try again or contact support if the problem persists.'

type SignInWithGoogleInput = {
  idToken: string
  redirectUrl?: string
}

type GoogleCredentialResponse = { credential?: string }

export const useSignInWithGoogle = (
  redirectUrl?: string,
): {
  handleSuccess: (credentialResponse: GoogleCredentialResponse) => void
  handleError: (err?: unknown) => void
  isLoading: boolean
} => {
  const { mutate: signInWithGoogleMutation, isLoading } = useMutation<void, Error, SignInWithGoogleInput>(
    async (input) => {
      await authService.signInWithGoogle({
        idToken: input.idToken,
        redirectUrl: input.redirectUrl ?? redirectUrl,
      })
    },
    {
      mutationKey: queryKeys.signInWithGoogle(),
      onMutate: () => authService.signupStarted('google'),
      onError: (err) => {
        logger.error('Google Sign In error', { error: err })
        toasterUtil.error(err.message ?? GOOGLE_SIGN_IN_ERROR_MESSAGE)
      },
    },
  )

  const handleSuccess = (credentialResponse: GoogleCredentialResponse): void => {
    const idToken = credentialResponse.credential
    if (!idToken) {
      logger.error('ID token not found in Google response', { credentialResponse })
      toasterUtil.error(GOOGLE_SIGN_IN_ERROR_MESSAGE)
      return
    }
    signInWithGoogleMutation({ idToken, redirectUrl })
  }

  const handleError = (err?: unknown): void => {
    logger.error('Google Sign In error', { err, clientId: config.google.clientId ? 'configured' : 'missing' })
    toasterUtil.error(GOOGLE_SIGN_IN_ERROR_MESSAGE)
  }

  return {
    handleSuccess,
    handleError,
    isLoading,
  }
}
